import React, { forwardRef, ReactNode, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ModalUnstyled } from '@mui/base';

import { JourneyEditPreviewOpenCloseTransition } from '@/features/journey/admin/atoms/JourneyEditPreviewOpenCloseTransition/JourneyEditPreviewOpenCloseTransition';
import { ModalProps } from '@/administration/pages/Journey/pages/EditJourney/modals/Preview/types';
import { IconButton } from '@/ui/Button';
import { ScreenSize } from '@/ui/theme/layout';
import { mediaQuery } from '@/ui/utils';

const CALCULATED_MODAL_BOX_MAX_HEIGHT = `calc(100vh - 200px)`;
export const CALCULATED_MENU_INSIDE_MODAL_MAX_HEIGHT = `calc(100vh - 200px - 80px - 32px)`;

const RootContainerStyled = styled(ModalUnstyled)`
  position: fixed;
  z-index: 1300;
  right: 0;
  top: 120px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainModalBoxStyled = styled.dialog<{ size?: ScreenSize }>`
  box-sizing: border-box;
  border: none;
  &:focus-visible {
    outline: none;
  }
  margin: 0;
  position: relative;
  background: ${(p) => p.theme.colors.neutral.background};
  ${(p) => p.theme.shadow.primary[8]}
  border-radius: 12px 12px;

  display: flex;
  flex-direction: column;
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }

  ${mediaQuery.until('tablet')} {
    max-width: calc(100vw - 168px);
    max-height: ${CALCULATED_MODAL_BOX_MAX_HEIGHT};
    padding: 80px 16px 0 16px;
  }

  ${mediaQuery.from('desktop')} {
    max-width: calc(100vw - 168px);
    max-height: ${CALCULATED_MODAL_BOX_MAX_HEIGHT};
    padding: 80px 8px 0 8px;
  }

  ${mediaQuery.from('widescreen')} {
    max-width: calc(100vw - 168px);
    max-height: ${CALCULATED_MODAL_BOX_MAX_HEIGHT};
    padding: 80px 60px 0 60px;
  }

  ${mediaQuery.from('fhd')} {
    max-width: calc(100vw - 168px);
    max-height: ${CALCULATED_MODAL_BOX_MAX_HEIGHT};
    padding: 80px 132px 0 132px;
  }
`;

const CloseButtonStyled = styled(IconButton).attrs({
  buttonVariant: 'outlined',
  icon: 'xmark',
})`
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);
  height: 36px;
  padding: 2px 0;
  z-index: 1301;
`;

const backdropTransition = {
  enter: keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  `,
  exit: keyframes`
    from {
      pointer-events: none;
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  `,
};

const BackdropStyled = styled.div<{ open: boolean }>`
  z-index: -1;
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  backdrop-filter: blur(1px);
  -webkit-tap-highlight-color: transparent;
  opacity: 0;
  animation-fill-mode: forwards;
  animation-duration: ${(p) => p.theme.transitions.normal}ms;
  animation-name: ${(p) => (p.open ? backdropTransition.enter : backdropTransition.exit)};
  background: ${({ theme }) => theme.tones.neutral[99]};
`;

const ConditionalWrapper = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    condition: boolean;
    wrapper: (children: React.ReactNode) => JSX.Element;
  }
>(({ children, condition, wrapper }, _) => (condition ? wrapper(children) : <>{children}</>));
ConditionalWrapper.displayName = 'ConditionalWrapper';

export const Modal = ({
  open,
  children,
  onClose,
  closeOnEscapeKey = true,
  size,
  hasTransition = true,
  className,
}: ModalProps): JSX.Element => {
  useEffect(() => {
    if (closeOnEscapeKey && onClose) {
      const close = ({ key }: KeyboardEvent) => key === 'Escape' && onClose();
      document.addEventListener('keyup', close);
      return () => document.removeEventListener('keyup', close);
    }
  }, [onClose, closeOnEscapeKey]);

  return (
    <RootContainerStyled
      open={open}
      closeAfterTransition
      slots={{ backdrop: BackdropStyled }}
      // We need a loose type here due to MUI's unprecise type defs.
      // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
      slotProps={{ backdrop: { open } as any, root: { 'data-testid': 'modalRoot' } as any }}
      className={className}
    >
      <ConditionalWrapper
        condition={hasTransition}
        wrapper={(children) => (
          <JourneyEditPreviewOpenCloseTransition in={open}>
            <>{children}</>
          </JourneyEditPreviewOpenCloseTransition>
        )}
      >
        {onClose && <CloseButtonStyled onClick={onClose} />}
        <MainModalBoxStyled size={size} tabIndex={-1}>
          {children}
        </MainModalBoxStyled>
      </ConditionalWrapper>
    </RootContainerStyled>
  );
};
